import React from 'react';
import { motion } from 'framer-motion';
import ContentPage from '../ContentPage';

const Services: React.FC = () => {
  // Define service features with icons and colors
  const mainFeatures = [
    {
      title: "Built for the Diaspora",
      description: "We focus exclusively on Afro-Caribbean creators and businesses abroad, providing a platform that understands your unique needs and cultural context.",
      icon: "🌍",
      color: "purple"
    },
    {
      title: "Multi-Category Marketplace",
      description: "Sell a diverse range of products including hair & beauty, art, fashion, books, food, drink, and more - all under one digital roof.",
      icon: "🛍️",
      color: "orange"
    },
    {
      title: "Culturally Aligned Customers",
      description: "Reach buyers who genuinely value heritage, authenticity, and storytelling - customers who are actively seeking the unique products you offer.",
      icon: "🤝",
      color: "yellow"
    },
    {
      title: "Simple Seller Setup",
      description: "Launch your store, upload products, and start selling with our intuitive platform designed for ease of use and maximum visibility.",
      icon: "🚀",
      color: "teal"
    }
  ];

  const additionalServices = [
    {
      title: "Marketing Support",
      description: "Benefit from our platform-wide marketing initiatives and promotional campaigns.",
      icon: "📣"
    },
    {
      title: "Business Analytics",
      description: "Access detailed insights about your store performance and customer behavior.",
      icon: "📊"
    },
    {
      title: "Secure Payments",
      description: "Enjoy reliable, secure payment processing with multiple options for your customers.",
      icon: "🔒"
    },
    {
      title: "Seller Community",
      description: "Connect with other Afro-Caribbean businesses for collaboration and support.",
      icon: "👥"
    }
  ];

  return (
    <ContentPage title="Our Services" accent="purple">
      {/* Intro section with subtle decorative element */}
      <div className="relative mb-10">
        <div className="absolute -top-8 -right-8 w-32 h-32 bg-gray-200 opacity-5 rounded-full blur-xl"></div>
        <h3 className="text-2xl font-bold mb-4 text-gray-800 relative z-10">✨ Why Join SokoVibe?</h3>
        <p className="text-gray-700 mb-6 relative z-10">Discover how our platform is specially designed to help Afro-Caribbean businesses thrive in the global marketplace.</p>
      </div>
      
      {/* Main features with elegant design */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        {mainFeatures.map((feature, index) => (
          <motion.div 
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
            className="relative border-b border-gray-100 pb-6"
          >
            <div className="flex items-start">
              <span className="text-2xl mr-4 text-gray-400">{feature.icon}</span>
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">
                  {feature.title}
                </h4>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Quote section */}
      <div className="mb-12 bg-gradient-to-r from-purple-50 via-orange-50 to-yellow-50 p-6 rounded-xl border border-orange-100 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-300 opacity-20 rounded-bl-full"></div>
        <div className="absolute bottom-0 left-0 w-16 h-16 bg-purple-300 opacity-20 rounded-tr-full"></div>
        
        <blockquote className="relative z-10">
          <p className="text-lg italic text-gray-700 mb-4">
          "At SokoVibe, we set out to build more than just a marketplace—we're creating a movement rooted in cultural pride and authentic connection. Seeing our platform help small businesses grow and reach the right audience has been the most rewarding part of this journey.""
          </p>
          <footer className="text-right">
            <p className="font-medium text-orange-700">— Founder</p>
          </footer>
        </blockquote>
      </div>
      
      {/* Additional services with elegant design */}
      <div className="mb-12">
        <h3 className="text-xl font-bold mb-6 text-gray-700">Additional Benefits</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {additionalServices.map((service, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 + (0.1 * index) }}
              className="flex items-center"
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mr-4">
                <span className="text-lg text-gray-500">{service.icon}</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-700">{service.title}</h4>
                <p className="text-sm text-gray-600">{service.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    
    </ContentPage>
  );
};

export default Services;
